// Seam over vault write delegation — the on-chain proxy grant that replaced
// the gateway's removed `/auth/delegates` API (vault-sdk 5.0.0 / account 2.0.0).
// `sdk.vault.delegate.grant/revoke` no longer exist: the durable authorization
// IS an on-chain `NonTransfer` proxy on the owner's account, delegate =
// vault-api's signing key, and `vault.ensure()` grants it by default during
// the claim (tokenless path). This seam exists for the OUT-of-claim surfaces
// that need to (re-)grant: the disconnected banner's "Re-authorize" button and
// the Settings storage control.
//
// WHY A SEAM rather than importing `@cef-ai/account/vault-delegate` inline at
// the call sites: (1) one mockable shape for the UI tests, mirroring
// `connect-agent.ts`; (2) the import is DYNAMIC, so `@cef-ai/chain` (and the
// papi stack behind it) loads only when a re-authorize actually runs — a
// static import here would move the whole chain bundle into the app's main
// chunk for every user, when the SDK goes out of its way (lazy import in
// `vault.ensure`) to avoid exactly that.
//
// No revoke counterpart ON PURPOSE: the removed gateway grant was revocable
// by design; an on-chain proxy is removed with a `Proxy.remove_proxy`
// extrinsic the SDK does not wrap yet. Until it does, revocation is a
// chain-explorer operation, and the UI must not offer a button for it.
import type { Signer } from '@cef-ai/signer';

export interface VaultDelegateGrantInput {
  /** The vault owner's chain-free signer — the proxy grantor. */
  signer: Signer;
  /** Cere node WS URL (runtime `rpcEndpoint`). */
  chainUrl: string;
  /** vault-api base URL — the delegate pubkey is read from `/api/v1/delegate-info`. */
  vaultApiUrl: string;
  /** Test seam for the fetch used by the delegate-info lookup. */
  fetchFn?: typeof fetch;
}

export interface VaultDelegateGrantResult {
  /** Hash of the submitted `Proxy.add_proxy` extrinsic; empty when none was needed. */
  txHash: string;
  /** True when the chain already carried a satisfying grant and no tx was sent. */
  alreadyPresent: boolean;
}

/**
 * Grant (or confirm) vault-api's NonTransfer write-delegation proxy.
 * Idempotent — a satisfying grant short-circuits with no transaction.
 */
export async function ensureVaultDelegateGrant(
  input: VaultDelegateGrantInput,
): Promise<VaultDelegateGrantResult> {
  const { ensureVaultDelegate } = await import('@cef-ai/account/vault-delegate');
  const { txHash, alreadyPresent } = await ensureVaultDelegate(input);
  return { txHash, alreadyPresent };
}
